// Compare two sets of BookNow entires and chose the most dominant based on their index
import { BookNowEntry, BookNowIndex, BookNowStatuses, JobBids, JSONApiDataObject } from 'Douleutaras/types/index';
import { AllBookNowStatuses, PositiveBookNowStatuses } from 'Douleutaras/constants/book-now';
import {
  BID_MILESTONES,
  BOOK_NOW_INDEXES,
  BOOK_NOW_STATUS_TO_INDEX, BOOK_NOW_STATUS_TO_MILESTONE_STEP,
  MILESTONE_TO_INDEX
} from 'Douleutaras/constants/ccf/need-help-options';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import {
  bidBuyerStatusSelectors,
  JOB_BIDS_STATUS_BUYER_MODEL_NAME
} from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import {
  jobBidsBookNowSelectors,
  JOB_BIDS_BOOK_NOW_MODEL_NAME
} from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';

export function selectBookNowEntriesWithLargestIndex(baseBookNowEntries: Array<BookNowIndex>, bookNowEntries: Array<BookNowIndex>)
  : Partial<Array<BookNowIndex>> {
  return baseBookNowEntries.length > 0 ?
    baseBookNowEntries
      .map((baseBookNowEntry, index) => {
        const bookNowEntry = bookNowEntries[index];

        return bookNowEntry?.index > baseBookNowEntry.index ? bookNowEntry : baseBookNowEntry;
      })
    : [];
}

export function getMostPositiveBookNowEntryByStatus(bookNowEntry: Array<BookNowIndex>): BookNowIndex {
  return bookNowEntry.reduce(
    (prevValue, currentValue) => {
      const isBigger = PositiveBookNowStatuses.indexOf(prevValue.status)
        > PositiveBookNowStatuses.indexOf(currentValue.status);

      return isBigger ? prevValue : currentValue;
    });
}

export function getMostProgressedBookNowEntry(bookNowEntry: Array<BookNowIndex>): BookNowIndex {
  return bookNowEntry.reduce(
    (prevValue, currentValue) => {
      const isBigger = AllBookNowStatuses.indexOf(prevValue.status)
        > AllBookNowStatuses.indexOf(currentValue.status);

      return isBigger ? prevValue : currentValue;
    });
}

interface BidEntryAttributes {
  type: typeof JOB_BIDS_STATUS_BUYER_MODEL_NAME | typeof JOB_BIDS_BOOK_NOW_MODEL_NAME;
  id: string;
  bookNowIndex: number;
  bookNowStatus: BookNowStatuses;
}

export const useMostAdvancedEntries = (proBookNowEntryIds: Array<string>, clientBookNowEntryIds: Array<string>):
  Array<BidEntryAttributes> => {

  let currentBookNowEntryFromBuyer;
  let currentBookNowEntryFromPro;

  // These should be in pairs
  const bookNowEntries = useAppSelector(state => state.jobBidsBookNow);
  const buyerChoiceEntries = useAppSelector(state => state.jobBidsStatusBuyer);

  return proBookNowEntryIds.map((proBookNowEntryId, index) => {
    const clientBookNowEntryId: string = clientBookNowEntryIds[index];
    const bookNowEntry = bookNowEntries.entities[proBookNowEntryId];
    const bidBuyerEntry = buyerChoiceEntries.entities[clientBookNowEntryId];

    const bidBuyerStatusMilestone = bidBuyerEntry?.attributes.milestone;
    const bidBuyerStatusStepIndex = MILESTONE_TO_INDEX[BID_MILESTONES[bidBuyerStatusMilestone]?.step];

    const noBookNowEntry = bookNowEntry === undefined;
    const noBidBuyerStatus = bidBuyerEntry === undefined || bidBuyerEntry.attributes.milestone === null;

    if (!noBidBuyerStatus) {
      currentBookNowEntryFromBuyer = BOOK_NOW_INDEXES
        .filter(milestone => milestone.status === bidBuyerStatusStepIndex)
        .pop();
    }

    if (!noBookNowEntry) {
      currentBookNowEntryFromPro = BOOK_NOW_INDEXES
        .filter(milestone => milestone.status === bookNowEntry.attributes.status)
        .pop();
    }

    const buyerReturn: BidEntryAttributes = {
      type: JOB_BIDS_STATUS_BUYER_MODEL_NAME,
      id: bidBuyerEntry?.id,
      bookNowIndex: currentBookNowEntryFromBuyer?.index,
      bookNowStatus: currentBookNowEntryFromBuyer?.status
    };
    const proReturn: BidEntryAttributes = {
      type: JOB_BIDS_BOOK_NOW_MODEL_NAME,
      id: bookNowEntry?.id,
      bookNowIndex: currentBookNowEntryFromPro?.index,
      bookNowStatus: currentBookNowEntryFromPro?.status
    };

    switch (true) {
      // No entry from PRO, has entry from Buyer
      case noBookNowEntry && !noBidBuyerStatus:
        return buyerReturn;
      // No entry from Buyer, has entry from PRO
      case noBidBuyerStatus && !noBookNowEntry:
        return proReturn;
      // Has both entries, select most advanced by index comparison
      case !noBidBuyerStatus && !noBookNowEntry:
        return currentBookNowEntryFromBuyer.index > currentBookNowEntryFromPro.index
          ? buyerReturn : proReturn;
      // No milestones selected by both PRO and Client.
      default:
        return;
    }

  });
};

// Given Pro's and Client's BookNow entry ids, return the MOST ADVANCED BOOK NOW ENTRY giving priority to the client
export const useMostAdvancedBookNowEntry = (proBookNowEntryIds: Array<string>, clientBookNowEntryIds: Array<string>)
  : Array<BookNowIndex> => {

  const mostAdvancedEntries = useMostAdvancedEntries(proBookNowEntryIds, clientBookNowEntryIds)
    .filter(entry => typeof entry !== 'undefined');
  const bookNowEntries = useAppSelector(state => state.jobBidsBookNow);
  const buyerChoiceEntries = useAppSelector(state => state.jobBidsStatusBuyer);

  return mostAdvancedEntries.map(mostAdvancedEntry => {
    switch (mostAdvancedEntry.type) {
      case JOB_BIDS_STATUS_BUYER_MODEL_NAME:
        const bidBuyerEntry = buyerChoiceEntries.entities[mostAdvancedEntry.id];
        const bidBuyerStatusMilestone = bidBuyerEntry?.attributes.milestone;
        const bidBuyerStatusStepIndex = MILESTONE_TO_INDEX[BID_MILESTONES[bidBuyerStatusMilestone]?.step];

        return BOOK_NOW_INDEXES
          .filter(milestone => milestone.status === bidBuyerStatusStepIndex)
          .pop();

      case JOB_BIDS_BOOK_NOW_MODEL_NAME:
        const bookNowEntry = bookNowEntries.entities[mostAdvancedEntry.id];

        return BOOK_NOW_INDEXES
          .filter(milestone => milestone.status === bookNowEntry.attributes.status)
          .pop();
      default:
        return BOOK_NOW_INDEXES.find(milestone => milestone.status === BookNowStatuses.ACTIVE);
    }
  });
};
